import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import Header from "../../../Common/Layout/Header";
import ModifyBusSearch from "../ModifyBusSearch/ModifyBusSearch1";
import { 
  FiBus, 
  FiFilter, 
  FiClock, 
  FiMapPin, 
  FiDollarSign,
  FiWifi,
  FiCoffee,
  FiBattery,
  FiAirplay,
  FiTv,
  FiWind,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiSliders,
  FiChevronRight
} from "react-icons/fi";
import { 
  BsSuitLg, 
  BsPersonBadge, 
  BsSnow, 
  BsUsbSymbol,
  BsPlug
} from "react-icons/bs";
import { MdOutlineNightShelter, MdOutlineChair } from "react-icons/md";
import { TbBusStop } from "react-icons/tb";
import styles from "./BusResults1.module.css";

const BusResults1 = ({ mode, onNavigate, templateId, params }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
 const initialSearchQuery = 
  params?.searchQuery || location.state?.searchQuery;
  const [currentSearch, setCurrentSearch] = useState(initialSearchQuery);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSort, setSelectedSort] = useState("price");
  const [expandedFilters, setExpandedFilters] = useState({
    type: true,
    amenities: false,
    price: true,
  });

  // Filter states
  const [filters, setFilters] = useState({
    busTypes: [],
    operators: [],
    priceRange: [0, 10000],
    amenities: [],
    rtc: false,
  });

  const [filteredBuses, setFilteredBuses] = useState([]);

  // API call to search buses
  const searchBuses = async (searchData) => {
    setLoading(true);
    setError(null);

    try {
      const requestData = {
        CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        Source: searchData.fromStation,
        Destination: searchData.toStation,
        DateOfJourney: searchData.travelDate,
        Passengers: 1,
        ReturnDate: null,
      };
      console.log("Searching buses with data:", requestData);
      const response = await apiService.post(config.SEARCHBUSES, requestData);
      console.log("Bus search response:", response);

      if (response && Array.isArray(response)) {
        setBuses(response);
        setFilteredBuses(response);
      } else {
        setBuses([]);
        setFilteredBuses([]);
      }
    } catch (err) {
      setError("Failed to fetch bus data. Please try again.");
      setBuses([]);
      setFilteredBuses([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle seat selection
  const handleSelectSeats = (bus) => {
    if (mode === "builder") {
      onNavigate({ name: "bus-seats", params: { bus, searchQuery: currentSearch } });
    } else if (mode === "preview") {
      navigate(`/template-preview/${templateId}/bus-seats`, { state: { bus, searchQuery: currentSearch } });
    } else {
      navigate("/template/bus/seats", { state: { bus, searchQuery: currentSearch } });
    }
  };

  // Format date
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  useEffect(() => {
    if (currentSearch) {
      searchBuses(currentSearch);
    }
  }, []);

  const handleModifySearch = (newSearchQuery) => {
    setCurrentSearch(newSearchQuery);
    searchBuses(newSearchQuery);
  };

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...buses];

    // Apply all filters
    if (filters.busTypes.length > 0) {
      filtered = filtered.filter((bus) =>
        filters.busTypes.includes(bus.busType)
      );
    }

    if (filters.operators.length > 0) {
      filtered = filtered.filter((bus) =>
        filters.operators.includes(bus.operatorName)
      );
    }

    const minPrice = parseFloat(filters.priceRange[0]);
    const maxPrice = parseFloat(filters.priceRange[1]);
    filtered = filtered.filter((bus) => {
      const basePrice = getBasePrice(bus);
      return basePrice >= minPrice && basePrice <= maxPrice;
    });

    if (filters.amenities.length > 0) {
      filtered = filtered.filter((bus) => {
        const busAmenities = bus.busAmenities || [];
        return filters.amenities.every((amenity) =>
          busAmenities.includes(amenity)
        );
      });
    }

    if (filters.rtc) {
      filtered = filtered.filter((bus) => bus.rtc === true);
    }

    // Apply sorting
    if (selectedSort === "price") {
      filtered.sort((a, b) => getBasePrice(a) - getBasePrice(b));
    } else if (selectedSort === "price-desc") {
      filtered.sort((a, b) => getBasePrice(b) - getBasePrice(a));
    } else if (selectedSort === "departure") {
      filtered.sort((a, b) => (a.departureTime || "").localeCompare(b.departureTime || ""));
    } else if (selectedSort === "duration") {
      filtered.sort((a, b) => (a.durationInMins || 0) - (b.durationInMins || 0));
    } else if (selectedSort === "seats") {
      filtered.sort((a, b) => (b.availableSeats || 0) - (a.availableSeats || 0));
    }

    setFilteredBuses(filtered);
  }, [filters, buses, selectedSort]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter((item) => item !== value)
        : [...prev[filterType], value],
    }));
  };

  const handleRTCFilterChange = (checked) => {
    setFilters((prev) => ({
      ...prev,
      rtc: checked,
    }));
  };

  const handlePriceRangeChange = (min, max) => {
    setFilters((prev) => ({
      ...prev,
      priceRange: [min, max],
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      busTypes: [],
      operators: [],
      priceRange: [0, 10000],
      amenities: [],
      rtc: false,
    });
  };

  const toggleFilter = (filter) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filter]: !prev[filter]
    }));
  };

  const getAmenityIcon = (amenity) => {
    const amenityLower = amenity.toLowerCase();
    if (amenityLower.includes('wifi')) return <FiWifi />;
    if (amenityLower.includes('ac') || amenityLower.includes('a/c')) return <FiWind />;
    if (amenityLower.includes('charging') || amenityLower.includes('usb')) return <BsUsbSymbol />;
    if (amenityLower.includes('tv') || amenityLower.includes('entertainment')) return <FiTv />;
    if (amenityLower.includes('snack') || amenityLower.includes('food')) return <FiCoffee />;
    if (amenityLower.includes('toilet') || amenityLower.includes('washroom')) return <FiAirplay />;
    if (amenityLower.includes('blanket')) return <MdOutlineNightShelter />;
    return <FiBattery />;
  };

  const getBusTypeColor = (busType) => {
    const type = busType?.toLowerCase() || "";
    if (type.includes('sleeper')) return styles.typeSleeper;
    if (type.includes('semi')) return styles.typeSemiSleeper;
    if (type.includes('seater')) return styles.typeSeater;
    if (type.includes('luxury')) return styles.typeLuxury;
    if (type.includes('ac')) return styles.typeAC;
    return styles.typeDefault;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getBasePrice = (bus) => {
    if (!bus.basePrice) return 0;
    try {
      let price;
      if (typeof bus.basePrice === "string") {
        const prices = bus.basePrice.split(",");
        price = parseFloat(prices[0]);
      } else {
        price = parseFloat(bus.basePrice);
      }
      return isNaN(price) ? 0 : Math.round(price);
    } catch (error) {
      return 0;
    }
  };

  const getAllUniqueAmenities = () => {
    const allAmenities = new Set();
    buses.forEach((bus) => {
      if (bus.busAmenities && Array.isArray(bus.busAmenities)) {
        bus.busAmenities.forEach((amenity) => allAmenities.add(amenity));
      }
    });
    return Array.from(allAmenities);
  };

  const getActiveFilterCount = () => {
    let count = filters.busTypes.length + filters.amenities.length;
    if (filters.rtc) count += 1;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 10000) count += 1;
    return count;
  };

  if (!currentSearch) {
    return (
      <div className={styles.wrapper}>
       
        <div className={styles.errorPage}>
          <div className={styles.container}>
            <div className={styles.errorCard}>
              {/* <FiBus className={styles.errorIcon} /> */}
              <h2>No Search Data Found</h2>
              <p>Please search for buses to continue</p>
              <button onClick={() => navigate('/')} className={styles.primaryButton}>
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      
      <div className={styles.mainContent}>
        <div className={styles.container}>
          {/* Search Summary Banner */}
         

          {/* Modify Search Component - Fixed positioning container */}
          <div className={styles.modifySearchContainer}>
            <ModifyBusSearch
              searchQuery={currentSearch}
              onSearch={handleModifySearch}
            />
          </div>

          {loading && (
            <div className={styles.loadingContainer}>
              <div className={styles.busAnimation}>
                {/* <FiBus className={styles.movingBus} /> */}
              </div>
              <p>Searching for the best buses...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorContainer}>
              <FiX />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && (
            <div className={styles.resultsContainer}>
              {/* Filter Sidebar */}
              <div className={`${styles.filterSidebar} ${showFilters ? styles.show : ''}`}>
                <div className={styles.filterHeader}>
                  <h3>
                    <FiFilter />
                    Filter Results
                  </h3>
                  <button onClick={() => setShowFilters(false)} className={styles.closeFilter}>
                    <FiX />
                  </button>
                </div>

                <div className={styles.filterBody}>
                  {/* Active Filters */}
                  {getActiveFilterCount() > 0 && (
                    <div className={styles.activeFilters}>
                      <div className={styles.activeFiltersHeader}>
                        <span>Active Filters</span>
                        <button onClick={clearAllFilters}>Clear all</button>
                      </div>
                      <div className={styles.activeFilterTags}>
                        {filters.busTypes.map(type => (
                          <span key={type} className={styles.filterTag}>
                            {type}
                            <FiX onClick={() => handleFilterChange('busTypes', type)} />
                          </span>
                        ))}
                        {filters.amenities.map(amenity => (
                          <span key={amenity} className={styles.filterTag}>
                            {amenity}
                            <FiX onClick={() => handleFilterChange('amenities', amenity)} />
                          </span>
                        ))}
                        {filters.rtc && (
                          <span className={styles.filterTag}>
                            RTC
                            <FiX onClick={() => handleRTCFilterChange(false)} />
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* RTC Filter */}
                  <div className={styles.filterGroup}>
                    <label className={styles.switchLabel}>
                      <span>Government Buses (RTC)</span>
                      <div className={styles.switch}>
                        <input
                          type="checkbox"
                          checked={filters.rtc}
                          onChange={(e) => handleRTCFilterChange(e.target.checked)}
                        />
                        <span className={styles.slider}></span>
                      </div>
                    </label>
                  </div>

                  {/* Bus Type Filter */}
                  <div className={styles.filterGroup}>
                    <div 
                      className={styles.filterGroupHeader}
                      onClick={() => toggleFilter('type')}
                    >
                      <span>Bus Type</span>
                      {expandedFilters.type ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                    {expandedFilters.type && (
                      <div className={styles.filterOptions}>
                        {Array.from(
                          new Set(
                            buses.map((bus) => bus.busType).filter(Boolean)
                          )
                        ).map((type) => (
                          <label key={type} className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={filters.busTypes.includes(type)}
                              onChange={() => handleFilterChange("busTypes", type)}
                            />
                            <span className={styles.checkboxCustom}></span>
                            <span className={styles.checkboxText}>{type}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Price Range Filter */}
                  <div className={styles.filterGroup}>
                    <div 
                      className={styles.filterGroupHeader}
                      onClick={() => toggleFilter('price')}
                    >
                      <span>Price Range</span>
                      {expandedFilters.price ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                    {expandedFilters.price && (
                      <div className={styles.priceFilter}>
                        <div className={styles.priceInputs}>
                          <div className={styles.priceInput}>
                            <span>Min</span>
                            <input
                              type="number"
                              value={filters.priceRange[0]}
                              onChange={(e) => handlePriceRangeChange(
                                parseInt(e.target.value) || 0,
                                filters.priceRange[1]
                              )}
                              min="0"
                              max="10000"
                            />
                          </div>
                          <span className={styles.priceSeparator}>-</span>
                          <div className={styles.priceInput}>
                            <span>Max</span>
                            <input
                              type="number"
                              value={filters.priceRange[1]}
                              onChange={(e) => handlePriceRangeChange(
                                filters.priceRange[0],
                                parseInt(e.target.value) || 10000
                              )}
                              min="0"
                              max="10000"
                            />
                          </div>
                        </div>
                        <input
                          type="range"
                          className={styles.priceRange}
                          min="0"
                          max="10000"
                          step="100"
                          value={filters.priceRange[1]}
                          onChange={(e) => handlePriceRangeChange(
                            filters.priceRange[0],
                            parseInt(e.target.value)
                          )}
                        />
                      </div>
                    )}
                  </div>

                  {/* Amenities Filter */}
                  <div className={styles.filterGroup}>
                    <div 
                      className={styles.filterGroupHeader}
                      onClick={() => toggleFilter('amenities')}
                    >
                      <span>Amenities</span>
                      {expandedFilters.amenities ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                    {expandedFilters.amenities && (
                      <div className={styles.amenitiesGrid}>
                        {getAllUniqueAmenities().map((amenity) => (
                          <label key={amenity} className={styles.amenityCard}>
                            <input
                              type="checkbox"
                              checked={filters.amenities.includes(amenity)}
                              onChange={() => handleFilterChange("amenities", amenity)}
                            />
                            <span className={styles.amenityIcon}>
                              {getAmenityIcon(amenity)}
                            </span>
                            <span className={styles.amenityName}>{amenity}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Results Main Content */}
              <div className={styles.resultsMain}>
                {/* Results Header */}
                <div className={styles.resultsHeader}>
                  <div className={styles.resultsStats}>
                    <h2>{filteredBuses.length} Buses Found</h2>
                    <p>{currentSearch.fromStation} to {currentSearch.toStation}</p>
                  </div>
                  <div className={styles.resultsControls}>
                    <select 
                      className={styles.sortSelect}
                      value={selectedSort}
                      onChange={(e) => setSelectedSort(e.target.value)}
                    >
                      <option value="price">Price: Low to High</option>
                      <option value="price-desc">Price: High to Low</option>
                      <option value="departure">Departure Time</option>
                      <option value="duration">Duration</option>
                      <option value="seats">Available Seats</option>
                    </select>
                  </div>
                </div>

                {/* Bus List */}
                {filteredBuses.length === 0 ? (
                  <div className={styles.noResults}>
                    <TbBusStop className={styles.noResultsIcon} />
                    <h3>No Buses Available</h3>
                    <p>Try adjusting your filters or search criteria</p>
                    <button onClick={clearAllFilters} className={styles.clearFiltersBtn}>
                      Clear All Filters
                    </button>
                  </div>
                ) : (
                  <div className={styles.busList}>
                    {filteredBuses.map((bus, index) => (
                      <div
                        key={bus.routeScheduleId || `bus-${index}`}
                        className={styles.busCard}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className={styles.busCardContent}>
                          {/* Left Section - Time & Duration */}
                          <div className={styles.timeSection}>
                            <div className={styles.timeBlock}>
                              <span className={styles.time}>{bus.departureTime || "N/A"}</span>
                              <span className={styles.location}>{currentSearch.fromStation}</span>
                            </div>
                            <div className={styles.durationBlock}>
                              <FiClock />
                              <span>{formatDuration(bus.durationInMins)}</span>
                            </div>
                            <div className={styles.timeBlock}>
                              <span className={styles.time}>{bus.arrivalTime || "N/A"}</span>
                              <span className={styles.location}>{currentSearch.toStation}</span>
                            </div>
                          </div>

                          {/* Middle Section - Bus Info & Amenities */}
                          <div className={styles.busInfoSection}>
                            <div className={styles.operatorRow}>
                              <h3>{bus.operatorName || "Unknown Operator"}</h3>
                              <div className={styles.busTypeTags}>
                                <span className={`${styles.busTypeTag} ${getBusTypeColor(bus.busType)}`}>
                                  {bus.busType || "Standard"}
                                </span>
                                {bus.rtc && (
                                  <span className={`${styles.busTypeTag} ${styles.rtcTag}`}>
                                    RTC
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Compact Amenities Row */}
                            {bus.busAmenities && bus.busAmenities.length > 0 && (
                              <div className={styles.amenitiesRow}>
                                {bus.busAmenities.slice(0, 5).map((amenity, idx) => (
                                  <span key={idx} className={styles.amenityIcon} title={amenity}>
                                    {getAmenityIcon(amenity)}
                                  </span>
                                ))}
                                {bus.busAmenities.length > 5 && (
                                  <span className={styles.moreAmenities} title={bus.busAmenities.slice(5).join(', ')}>
                                    +{bus.busAmenities.length - 5}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Right Section - Seats & Price */}
                          <div className={styles.actionSection}>
                            <div className={styles.seatsInfo}>
                              <BsPersonBadge />
                              <span className={`${styles.seatCount} ${
                                (bus.availableSeats || 0) > 20 ? styles.seatExcellent :
                                (bus.availableSeats || 0) > 10 ? styles.seatGood :
                                (bus.availableSeats || 0) > 5 ? styles.seatLimited :
                                styles.seatCritical
                              }`}>
                                {bus.availableSeats || 0} left
                              </span>
                            </div>
                            <div className={styles.priceBlock}>
                              <span className={styles.price}>₹{getBasePrice(bus)}</span>
                            </div>
                            <button
                              className={styles.selectBtn}
                              disabled={(bus.availableSeats || 0) === 0}
                              onClick={() => handleSelectSeats(bus)}
                            >
                              {(bus.availableSeats || 0) === 0 ? "Sold Out" : "Select"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusResults1;